import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryProvider } from './api/QueryProvider';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { RootLayout, ProtectedRoute, MainLayout, ProtectedOrganizationRoute } from './App';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import PasswordLost from './components/Auth/PasswordLost';
import PasswordReset from './components/Auth/PasswordReset';
import MfaLost from './components/Auth/MfaLost';
import MfaReset from './components/Auth/MfaReset';
import Dashboard from './components/Dashboard/Dashboard';
import EntityLayout from './components/Entity/EntityLayout';
import EntityIdentity from './components/Entity/EntityIdentity';
import EntityAlerts from './components/Entity/EntityAlerts';
import EntityStructure from './components/Entity/Structure/EntityStructure';
import Alerts from './components/Alerts/Alerts';
import Billing from './components/Billing/Billing';
import SettingsLayout from './components/Settings/SettingsLayout';
import ProfileSettings from './components/Settings/ProfileSettings';
import SecuritySettings from './components/Settings/SecuritySettings';
import NotificationSettings from './components/Settings/NotificationSettings';
import OrganizationUsers from './components/Settings/OrganizationUsers';
import AlertRiskLevelsSettings from './components/Settings/AlertRiskLevelsSettings';
import { OrganizationsList, CreateOrganization } from './components/Organizations';
import EntitiesList from './components/Entities/EntitiesList';
import './index.css';

// Create the router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Auth routes (public)
      {
        path: 'auth',
        children: [
          { index: true, element: <Login /> },
          { path: 'signup', element: <Signup /> },
          { path: 'password-lost', element: <PasswordLost /> },
          { path: 'password-reset', element: <PasswordReset /> },
          { path: 'mfa-lost', element: <MfaLost /> },
          { path: 'mfa-reset', element: <MfaReset /> },
        ],
      },
      // Protected routes with Organization Provider
      {
        element: (<ProtectedRoute>
          <OrganizationProvider>
            <MainLayout />
          </OrganizationProvider>
        </ProtectedRoute>),
        children: [
          // Root redirect to organizations
          { index: true, element: <Navigate to="/organizations" replace /> },
          
          // Organization management routes
          { path: 'organizations', handle: { title: 'Organisations' }, element: <OrganizationsList /> },
          { path: 'organizations/create', handle: { title: 'Créer une organisation' }, element: <CreateOrganization /> },
          
          // Organization-scoped routes
          {
            path: ':organizationId',
            element: <ProtectedOrganizationRoute />,
            children: [
              { path: 'dashboard', handle: { title: 'Tableau de bord' }, element: <Dashboard /> },
              { path: 'entities', handle: { title: 'Entités' }, element: <EntitiesList /> },
              {
                path: 'entity/:clientId',
                handle: { title: 'Entité' },
                element: <EntityLayout />,
                children: [
                  { index: true, element: <EntityIdentity /> },
                  { path: 'alerts', element: <EntityAlerts /> },
                  { path: 'structure', element: <EntityStructure /> },
                ],
              },
              { path: 'alerts', handle: { title: 'Alertes' }, element: <Alerts /> },
              { path: 'billing', handle: { title: 'Facturation' }, element: <Billing /> },
            ],
          },
          
          // Settings routes
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, handle: { title: 'Paramètres' }, element: <ProfileSettings /> },
              { path: 'security', handle: { title: 'Sécurité' }, element: <SecuritySettings /> },
              { path: 'notifications', handle: { title: 'Notifications' }, element: <NotificationSettings /> },
              { path: ':organizationId/users', handle: { title: 'Utilisateurs' }, element: <ProtectedOrganizationRoute><OrganizationUsers /></ProtectedOrganizationRoute> },
              { path: ':organizationId/alert-risk-levels', handle: { title: 'Niveaux de risque des alertes' }, element: <ProtectedOrganizationRoute><AlertRiskLevelsSettings /></ProtectedOrganizationRoute> },
            ],
          },
        ]
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryProvider>
      <RouterProvider router={router} />
    </QueryProvider>
  </StrictMode>
);
